import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout, LegalSection } from "@/components/legal-layout";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — the Vibing Company®" },
      { name: "description", content: "How the Vibing Company® collects, uses, and protects your personal data under GDPR." },
      { property: "og:title", content: "Privacy Policy — the Vibing Company®" },
      ...ogImageMeta("privacy", "Share card: privacy policy"),
      { property: "og:description", content: "How the Vibing Company® collects, uses, and protects your personal data under GDPR." },
    ],
    links: [{ rel: "canonical", href: "/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalLayout kicker="01.PRIVACY" title="Privacy Policy" updated="June 2026">
      <LegalSection label="01" title="Introduction">
        <p>
          the Vibing Company® ("we", "us", "our") respects your privacy. This policy describes
          what personal data we collect when you visit vibing.inc or engage our services, how we
          use it, and the rights you have under the EU General Data Protection Regulation (GDPR).
        </p>
        <p>
          Controller: Jonathan Osburg · St.-Klara-Str. 7A, 82377 Penzberg, Germany ·{" "}
          <a href="mailto:jon@vibing.inc" className="underline">jon@vibing.inc</a>
        </p>
      </LegalSection>

      <LegalSection label="02" title="Data we collect">
        <p>We collect only what we need to run the studio and reply to you:</p>
        <ul className="list-none space-y-2">
          <li>— Contact data you submit (name, email, project URL, message)</li>
          <li>— Chat transcripts from the intake assistant</li>
          <li>— Billing data processed by Stripe (we never see card numbers)</li>
          <li>— Technical logs (IP, user agent, timestamps) for security and abuse prevention</li>
        </ul>
      </LegalSection>

      <LegalSection label="03" title="How we use your data">
        <p>
          We use your data to respond to enquiries, scope and deliver work you commission, issue
          invoices, comply with tax and accounting law, and protect the service from abuse. We do
          not sell your data and do not use it for advertising profiling.
        </p>
      </LegalSection>

      <LegalSection label="04" title="Legal basis (GDPR)">
        <ul className="list-none space-y-2">
          <li>— Art. 6(1)(b) — performance of a contract you requested</li>
          <li>— Art. 6(1)(c) — legal obligations (tax, accounting)</li>
          <li>— Art. 6(1)(f) — our legitimate interest in security and answering your messages</li>
          <li>— Art. 6(1)(a) — your consent, where required (cookies, marketing)</li>
        </ul>
      </LegalSection>

      <LegalSection label="05" title="Data retention">
        <p>
          Enquiry data is kept for up to 24 months after our last contact. Contracts, invoices and
          related records are kept for 10 years as required by German commercial and tax law (§§
          147 AO, 257 HGB). Server logs are rotated within 30 days.
        </p>
      </LegalSection>

      <LegalSection label="06" title="Data sharing">
        <p>We share data only with carefully selected processors acting on our instructions:</p>
        <ul className="list-none space-y-2">
          <li>— Stripe (payments)</li>
          <li>— Supabase (hosting and database)</li>
          <li>— Lovable AI gateway (intake assistant inference)</li>
          <li>— Email and analytics providers strictly necessary to operate the site</li>
        </ul>
        <p>
          Where a processor is outside the EU/EEA, transfers rely on Standard Contractual Clauses
          and supplementary safeguards.
        </p>
      </LegalSection>

      <LegalSection label="07" title="Your rights">
        <p>Under GDPR you may at any time:</p>
        <ul className="list-none space-y-2">
          <li>— Request access to your data (Art. 15)</li>
          <li>— Correct inaccurate data (Art. 16)</li>
          <li>— Have data deleted (Art. 17)</li>
          <li>— Restrict or object to processing (Art. 18, 21)</li>
          <li>— Receive your data in a portable format (Art. 20)</li>
          <li>— Withdraw consent at any time</li>
          <li>— Lodge a complaint with a supervisory authority</li>
        </ul>
        <p>
          To exercise any right, email{" "}
          <a href="mailto:jon@vibing.inc" className="underline">jon@vibing.inc</a>.
        </p>
      </LegalSection>

      <LegalSection label="08" title="Cookies">
        <p>
          We use a small number of strictly necessary cookies and, with your consent, basic
          analytics. See our{" "}
          <a href="/cookies" className="underline">Cookie Policy</a> for details.
        </p>
      </LegalSection>

      <LegalSection label="09" title="Changes">
        <p>
          We update this policy as our service evolves. Material changes will be announced on this
          page with a new "Last updated" date.
        </p>
      </LegalSection>

      <LegalSection label="10" title="Contact">
        <p>
          Questions about your privacy: write to{" "}
          <a href="mailto:jon@vibing.inc" className="underline">jon@vibing.inc</a> or the
          postal address above.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
